// Risk-Free Rate Lookup — PSD §3.5 (F3.5). Builder Step 5.

import { useState } from 'react';
import { lookupRiskFree, TREASURY_CURVE } from '@backsolve/shared-logic/riskFreeLookup';
import { ExpressionInput, NumericDisplay } from 'calc-controls';
import { Tooltip } from '../common/Tooltip';

interface RiskFreeLookupProps {
  timeToLiquidity: number;
  riskFreeRate: number;
  onChange: (rate: number) => void;
}

export function RiskFreeLookup({ timeToLiquidity, riskFreeRate, onChange }: RiskFreeLookupProps) {
  const [showCurve, setShowCurve] = useState(false);
  const implied = lookupRiskFree(timeToLiquidity);

  return (
    <div className="risk-free-lookup">
      <div className="form-group">
        <ExpressionInput
          label={
            <>
              Risk-Free Rate
              <Tooltip
                label="Continuously-compounded risk-free rate (r) used in Black-Scholes. The Prefill button interpolates the embedded US Treasury curve at the chosen time-to-liquidity."
                ariaLabel="Help: Risk-Free Rate"
              />
            </>
          }
          value={riskFreeRate}
          onChange={onChange}
          min={0}
          decimals={4}
          id="rfr"
        />
      </div>

      <div className="form-row">
        <button className="btn btn-secondary" onClick={() => onChange(implied)}>
          Prefill from Treasury Curve ({(implied * 100).toFixed(2)}%)
        </button>
        <button className="btn btn-ghost" onClick={() => setShowCurve(!showCurve)}>
          {showCurve ? 'Hide' : 'Show'} Curve
        </button>
      </div>

      {showCurve && (
        <table className="data-table mt-3">
          <thead>
            <tr>
              <th>Maturity (years)</th>
              <th>Yield</th>
            </tr>
          </thead>
          <tbody>
            {TREASURY_CURVE.map((p) => (
              <tr key={p.maturity} className={p.maturity === timeToLiquidity ? 'row-selected' : ''}>
                <td>{p.maturity}</td>
                <td>{(p.yield * 100).toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="field-hint">
        Implied rate for {timeToLiquidity}-year horizon:{' '}
        <NumericDisplay inline value={implied * 100} unit="%" decimals={2} />
      </div>
    </div>
  );
}
